import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
	Box,
	Container,
	Grid,
	GridItem,
	VStack,
	HStack,
	Text,
	Heading,
	Link as ChakraLink,
	Image,
	Icon,
	Divider,
	SimpleGrid,
	Flex
} from '@chakra-ui/react';
import { FaFacebook, FaTwitter, FaGoogle, FaLinkedin, FaHeart, FaPhone, FaWhatsapp, FaCreditCard, FaUniversity, FaMobileAlt, FaLock } from 'react-icons/fa';
import API from '../Api';
import { GRADIENTS, COLORS } from '../constants/theme';
import { useSettings, usePages } from '../hooks';
import { createSafeHtml } from '../utils/sanitize';

// Social links constant - moved outside component for better performance
const SOCIAL_LINKS = [
	{ icon: FaFacebook, href: 'https://www.facebook.com/printster.in/', label: 'Facebook' },
	{ icon: FaTwitter, href: 'https://twitter.com/printsterin', label: 'Twitter' },
	{ icon: FaGoogle, href: 'https://plus.google.com/+PrintsterInOfficial', label: 'Google' },
	{ icon: FaLinkedin, href: 'https://www.linkedin.com/company/printster-in/', label: 'LinkedIn' }
];

const Footer = () => {
	// Use custom hooks for cached data
	const { settings: setting } = useSettings();
	const { pages: pagesList } = usePages();

	// Keep product list separate as it uses different endpoint
	const [productList, setProductList] = useState([]);

	useEffect(() => {
		loadCategoryProductData();
	}, []);

	const loadCategoryProductData = async () => {
		try {
			const res = await API.get('category/active-category-product/get');
			setProductList(res.data.data);
		} catch (error) {
			console.error('Error fetching category products:', error);
			setProductList([]);
		}
	};

	// Memoize renderPageLinks to prevent recalculation on every render
	const renderPageLinks = useMemo(() => (pageType) => {
		return pagesList
			.filter(x => x.page_type === pageType)
			.map((item, index) => (
				<ChakraLink
					key={index}
					as={item.page_content_type === 3 ? 'a' : Link}
					to={item.page_content_type === 3 ? undefined : `/information/${item.seo_name}`}
					href={item.page_content_type === 3 ? item.link : undefined}
					isExternal={item.page_content_type === 3}
					fontSize="xs"
					color="gray.400"
					textDecoration="none"
					display="block"
					_hover={{
						color: 'white',
						textDecoration: 'none',
						transform: 'translateX(4px)'
					}}
					transition="all 0.2s"
				>
					{item.title}
				</ChakraLink>
			));
	}, [pagesList]);

	return (
		<Box as="footer" bg="gray.900" color="white" mt="auto">
			{/* Main Footer Content */}
			<Container maxW="container.xl" py={8}>
				<SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
					{/* Company Info */}
					<VStack align="start" spacing={3}>
						<Box>
							<Image
								src={`${process.env.PUBLIC_URL}/Image/printster-logo-dark-scaled.png`}
								alt="Printster.in - Online Printing Services"
								h="44px"
								objectFit="contain"
							/>
						</Box>
						<Text fontSize="xs" color="gray.400" lineHeight="relaxed">
							India's trusted online printing service for documents, books, thesis printing, spiral binding, hard binding & more. Fast doorstep delivery across India with affordable pricing.
						</Text>
						<HStack spacing={2} pt={1}>
							{SOCIAL_LINKS.map((social, index) => (
								<ChakraLink
									key={index}
									href={social.href}
									isExternal
									display="flex"
									alignItems="center"
									justifyContent="center"
									w="32px"
									h="32px"
									borderRadius="md"
									bg="gray.800"
									color="gray.500"
									_hover={{
										bgGradient: GRADIENTS.primary,
										color: 'white',
										transform: 'translateY(-2px)'
									}}
									transition="all 0.2s"
									aria-label={social.label}
								>
									<Icon as={social.icon} boxSize={3.5} />
								</ChakraLink>
							))}
						</HStack>
					</VStack>

					{/* Quick Links */}
					<VStack align="start" spacing={2}>
						<Heading size="sm" color="white" fontWeight="700" mb={1}>
							Quick Links
						</Heading>
						{renderPageLinks('1')}
						<ChakraLink
							as={Link}
							to="/offers"
							fontSize="xs"
							color="gray.400"
							textDecoration="none"
							display="block"
							_hover={{
								color: 'white',
								transform: 'translateX(4px)'
							}}
							transition="all 0.2s"
						>
							Offers
						</ChakraLink>
						<ChakraLink
							as={Link}
							to="/contact"
							fontSize="xs"
							color="gray.400"
							textDecoration="none"
							display="block"
							_hover={{
								color: 'white',
								transform: 'translateX(4px)'
							}}
							transition="all 0.2s"
						>
							Contact
						</ChakraLink>
					</VStack>

					{/* Help & Support */}
					<VStack align="start" spacing={2}>
						<Heading size="sm" color="white" fontWeight="700" mb={1}>
							Help & Support
						</Heading>
						{renderPageLinks('2')}
						<ChakraLink
							as={Link}
							to="/faq/faq"
							fontSize="xs"
							color="gray.400"
							textDecoration="none"
							display="block"
							_hover={{
								color: 'white',
								transform: 'translateX(4px)'
							}}
							transition="all 0.2s"
						>
							FAQs
						</ChakraLink>
						<ChakraLink
							as={Link}
							to="/faq/payment-option"
							fontSize="xs"
							color="gray.400"
							textDecoration="none"
							display="block"
							_hover={{
								color: 'white',
								transform: 'translateX(4px)'
							}}
							transition="all 0.2s"
						>
							Payment Options
						</ChakraLink>
						<ChakraLink
							as={Link}
							to="/career"
							fontSize="xs"
							color="gray.400"
							textDecoration="none"
							display="block"
							_hover={{
								color: 'white',
								transform: 'translateX(4px)'
							}}
							transition="all 0.2s"
						>
							Career
						</ChakraLink>
					</VStack>

					{/* Contact Us */}
					<VStack align="start" spacing={3}>
						<Heading size="sm" color="white" fontWeight="700" mb={1}>
							Contact Us
						</Heading>

						{/* Phone Number with Icons */}
						<VStack align="start" spacing={1}>
							<HStack spacing={2}>
								<Icon as={FaPhone} color="green.400" boxSize={3.5} />
								<Icon as={FaWhatsapp} color="green.400" boxSize={3.5} />
								<ChakraLink
									href="tel:+919643230482"
									fontSize="sm"
									fontWeight="700"
									color="white"
									_hover={{ color: 'green.400' }}
									transition="color 0.2s"
								>
									+91-9643230482
								</ChakraLink>
							</HStack>
							<VStack align="start" spacing={0} pl={7}>
								<Text fontSize="xs" color="gray.400">
									Call & Whatsapp
								</Text>
								<Text fontSize="xs" color="gray.400">
									11:00AM to 6:00PM
								</Text>
								<Text fontSize="xs" color="gray.400">
									MON to SAT
								</Text>
							</VStack>
						</VStack>

						{/* Mobile Apps */}
						{(setting.android_url || setting.ios_url) && (
							<VStack align="start" spacing={1.5} pt={1}>
								<Text fontSize="xs" fontWeight="600" color="gray.400">
									Download App
								</Text>
								<HStack spacing={2}>
									{setting.android_url && (
										<ChakraLink href={setting.android_url} isExternal>
											<Image
												src="/Image/android.png"
												alt="Android App"
												h="32px"
												_hover={{ transform: 'scale(1.05)' }}
												transition="transform 0.2s"
											/>
										</ChakraLink>
									)}
									{setting.ios_url && (
										<ChakraLink href={setting.ios_url} isExternal>
											<Image
												src="/Image/ios.png"
												alt="iOS App"
												h="32px"
												_hover={{ transform: 'scale(1.05)' }}
												transition="transform 0.2s"
											/>
										</ChakraLink>
									)}
								</HStack>
							</VStack>
						)}
					</VStack>
				</SimpleGrid>

				{/* Products Section - Compact with Categories */}
				{productList.length > 0 && (
					<Box mt={6} pt={6} borderTop="1px" borderColor="gray.800">
						<Heading size="sm" color="white" fontWeight="700" mb={3}>
							Our Products
						</Heading>
						<VStack align="start" spacing={3} divider={<Divider borderColor="gray.800" />}>
							{productList.map((category, index) => (
								<Box key={index} w="full">
									<Text fontSize="xs" color="white" fontWeight="600" mb={2}>
										{category.name}
									</Text>
									<Flex flexWrap="wrap" gap={2}>
										{category.product && category.product.map((product, prodIndex) => (
											<React.Fragment key={prodIndex}>
												<ChakraLink
													as={Link}
													to={`/product/${category.seo_name}/${product.seo_name}`}
													fontSize="xs"
													color="gray.400"
													textDecoration="none"
													_hover={{
														color: 'transparent',
														bgGradient: GRADIENTS.primary,
														bgClip: 'text',
														textDecoration: 'none'
													}}
													transition="all 0.2s"
												>
													{product.name}
												</ChakraLink>
												{prodIndex < category.product.length - 1 && (
													<Text fontSize="xs" color="gray.600">|</Text>
												)}
											</React.Fragment>
										))}
									</Flex>
								</Box>
							))}
						</VStack>
					</Box>
				)}

				{/* Bottom Matter Section - More Compact */}
				{setting.abbou_bottom_matter && (
					<Box mt={6} pt={6} borderTop="1px" borderColor="gray.800">
						<Box
							fontSize="xs"
							color="gray.400"
							lineHeight="relaxed"
							dangerouslySetInnerHTML={createSafeHtml(setting.abbou_bottom_matter)}
							sx={{
								'& a': {
									color: 'gray.400',
									textDecoration: 'none',
									_hover: {
										color: 'transparent',
										bgGradient: GRADIENTS.primary,
										bgClip: 'text'
									}
								},
								'& p': {
									mb: 2,
									color: 'gray.400'
								},
								'& h1, & h2, & h3, & h4, & h5, & h6': {
									color: 'white',
									fontWeight: '600',
									mb: 2,
									fontSize: 'sm'
								}
							}}
						/>
					</Box>
				)}

				{/* Secure Payment Options Section */}
				<Box mt={8} pt={6} borderTop="1px" borderColor="gray.800">
					<SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
						<VStack align="start" spacing={3}>
							<HStack spacing={2}>
								<Icon as={FaLock} color="green.400" boxSize={4} />
								<Text fontSize="sm" color="gray.300" fontWeight="700">
									Secure Payment Options
								</Text>
							</HStack>
							<HStack spacing={4} flexWrap="wrap">
								{/* Payment method icons */}
								<HStack spacing={2} bg="white" px={3} py={2} borderRadius="md">
									<Icon as={FaCreditCard} color="blue.600" boxSize={5} />
									<Text fontSize="xs" color="gray.700" fontWeight="600">Cards</Text>
								</HStack>
								<HStack spacing={2} bg="white" px={3} py={2} borderRadius="md">
									<Icon as={FaMobileAlt} color="purple.600" boxSize={5} />
									<Text fontSize="xs" color="gray.700" fontWeight="600">UPI</Text>
								</HStack>
								<HStack spacing={2} bg="white" px={3} py={2} borderRadius="md">
									<Icon as={FaUniversity} color="green.600" boxSize={5} />
									<Text fontSize="xs" color="gray.700" fontWeight="600">Net Banking</Text>
								</HStack>
								<Image src="/Image/Razorpay_logo.png" alt="Powered by Razorpay" h="20px" opacity={0.8} />
							</HStack>
						</VStack>
						<VStack align={{ base: 'start', md: 'end' }} spacing={2}>
							<Text fontSize="sm" color="gray.400" textAlign={{ base: 'left', md: 'right' }}>
								Made With <Icon as={FaHeart} color="red.500" /> in India
							</Text>
						</VStack>
					</SimpleGrid>
				</Box>
			</Container>

			{/* Bottom Bar - Compact */}
			<Box bg="gray.950" py={3}>
				<Container maxW="container.xl">
					<Flex
						direction={{ base: 'column', md: 'row' }}
						justify="space-between"
						align="center"
						gap={2}
					>
						<Text fontSize="xs" color="gray.500" textAlign={{ base: 'center', md: 'left' }}>
							© 2019-2025{' '}
							<ChakraLink
								href="https://printster.in/"
								color="gray.400"
								_hover={{
									color: 'transparent',
									bgGradient: GRADIENTS.primary,
									bgClip: 'text'
								}}
								isExternal
							>
								Tech Udyam Solutions Pvt. Ltd.
							</ChakraLink>
							{' '}All rights reserved.
						</Text>
						<HStack spacing={3} fontSize="xs" color="gray.500">
							{renderPageLinks('3')}
						</HStack>
					</Flex>
				</Container>
			</Box>
		</Box>
	);
};

export default Footer;
